import axios from 'axios';
const API_URL = 'https://farmo.zuddo.biz/api';
// const API_URL = 'http://localhost:5000/api';
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const errorData = error.response?.data || { error: 'Network error' };

    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      if (!window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    
    return Promise.reject(errorData);
  }
);

const createFormData = (data) => {
  const formData = new FormData();
  
  Object.keys(data).forEach(key => {
    const value = data[key];
    
    if (value === null || value === undefined) {
      return;
    }
    
    if (key === 'image' || key === 'logo') {
      if (value instanceof File) {
        formData.append(key, value);
      }
    } else if (key === 'images' && Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
    } else if (typeof value === 'object' && value !== null) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  });
  
  return formData;
};

export const authAPI = {
  login: (email, password) => 
    api.post('/auth/login', { email, password }),
  
  register: (userData) => 
    api.post('/auth/register', userData),
  
  getCurrentUser: () => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      return Promise.reject({ error: 'No authenticated user' });
    }
    
    try {
      return Promise.resolve({ 
        success: true, 
        user: JSON.parse(user) 
      });
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return Promise.reject({ error: 'Session expired' });
    }
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return Promise.resolve({ success: true });
  },
  
  forgotPassword: (email) => 
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (token, password) => 
    api.post('/auth/reset-password', { token, password }),
  
  updateProfile: (userData) => 
    api.put('/auth/profile', userData),
  
  changePassword: (currentPassword, newPassword) => 
    api.put('/auth/change-password', { currentPassword, newPassword }),
};

export const productAPI = {
  getProducts: (params = {}) => 
    api.get('/products/home', { params }),
  
  getProduct: (id) => 
    api.get(`/products/${id}`),
  
  getProductBySlug: (slug) => 
    api.get(`/products/slug/${slug}`),
  
  createProduct: (productData) => {
    const formData = createFormData(productData);
    return api.post('/products/home', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  

  updateProduct: (id, productData) => {
    const formData = createFormData(productData);
    return api.put(`/products/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  deleteProduct: (id) => 
    api.delete(`/products/${id}`),
  
  getCategories: () => 
    api.get('/products/categories'),
  
  getFeaturedProducts: (limit = 8) => 
    api.get('/products/featured', { params: { limit } }),
  
  getBestSellers: (limit = 8) => 
    api.get('/products/best-sellers', { params: { limit } }),
  
  getNewProducts: (limit = 8) => 
    api.get('/products/new', { params: { limit } }),
  
  searchProducts: (query, params = {}) => 
    api.get('/products/search', { params: { query, ...params } }),
  
  // Admin only - batch operations
  bulkDeleteProducts: (ids) => 
    api.post('/products/bulk-delete', { ids }),
  
  bulkUpdateProducts: (ids, data) => 
    api.post('/products/bulk-update', { ids, data }),
};

export const sponsorAPI = {
  getSponsors: (params = {}) => 
    api.get('/sponsors/home', { params }),
  
  getSponsor: (id) => 
    api.get(`/sponsors/${id}`),
  
  createSponsor: (sponsorData) => {
    const formData = createFormData(sponsorData);
    return api.post('/sponsors/home', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  updateSponsor: (id, sponsorData) => {
    const formData = createFormData(sponsorData);
    return api.put(`/sponsors/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  deleteSponsor: (id) => 
    api.delete(`/sponsors/${id}`),
  
  getCategories: () => 
    api.get('/sponsors/categories'),
  
  // Admin only
  reorderSponsors: (orderData) => 
    api.put('/sponsors/reorder', orderData),
};

export const contentAPI = {
    
  getContent: () => 
    
    api.get('/content/home'),
  
  getContentBySection: (section) => 
    api.get(`/content/${section}`),
  
  updateContent: (section, data) => 
    api.put(`/content/${section}`, data),
  
  updateMultipleSections: (sectionsData) => 
    api.put('/content/bulk-update', { sections: sectionsData }),

  getServices: () => 
    api.get('/content/services'),

  getFeatures: () => 
    api.get('/content/features'),

  getHomeImages: () => 
    api.get('/content/home-images'),

  getAboutImages: () => 
    api.get('/content/about-images'),
    
  
  resetToDefault: () => 
    api.post('/content/reset-default'),
};

export const appointmentAPI = {
  getAppointments: (params = {}) => 
    api.get('/appointments', { params }),
  
  getAppointment: (id) => 
    api.get(`/appointments/${id}`),
  
  createAppointment: (appointmentData) => 
    api.post('/appointments/public', appointmentData),
  
  updateAppointment: (id, appointmentData) => 
    api.put(`/appointments/${id}`, appointmentData),
  
  updateStatus: (id, status, notes) => 
    api.put(`/appointments/${id}/status`, { status, notes }),
  
  deleteAppointment: (id) => 
    api.delete(`/appointments/${id}`),
  
  getTodaysAppointments: () => 
    api.get('/appointments/today'),
  
  getStats: () => 
    api.get('/appointments/stats'),
  
  // Admin only
  bulkUpdateStatus: (ids, status) => 
    api.post('/appointments/bulk-status', { ids, status }),
  
  exportAppointments: (params = {}) => 
    api.get('/appointments/export', { 
      params,
      responseType: 'blob' // For file downloads
    }),
};

export const messageAPI = {
  getMessages: (params = {}) => 
    api.get('/messages/home', { params }),
  
  getMessage: (id) => 
    api.get(`/messages/${id}`),
  
  createMessage: (messageData) => 
    api.post('/messages/public', messageData),
  
  markAsRead: (id) => 
    api.put(`/messages/${id}/read`),
  
  markAsUnread: (id) => 
    api.put(`/messages/${id}/unread`),
  
  replyToMessage: (id, replyMessage) => 
    api.post(`/messages/${id}/reply`, { reply_message: replyMessage }),
  
  deleteMessage: (id) => 
    api.delete(`/messages/${id}`),
  
  getStats: () => 
    api.get('/messages/stats'),
  
  // Admin only
  bulkMarkAsRead: (ids) => 
    api.post('/messages/bulk-read', { ids }),
  
  bulkDelete: (ids) => 
    api.post('/messages/bulk-delete', { ids }),
};

export const userAPI = {
  getUsers: (params = {}) => 
    api.get('/users', { params }),
  
  getUser: (id) => 
    api.get(`/users/${id}`),
  
  createUser: (userData) => 
    api.post('/users', userData),
  
  updateUser: (id, userData) => 
    api.put(`/users/${id}`, userData),
  
  deleteUser: (id) => 
    api.delete(`/users/${id}`),
  
  updateUserRole: (id, role) => 
    api.put(`/users/${id}/role`, { role }),
  
  toggleUserStatus: (id) => 
    api.put(`/users/${id}/toggle-status`),
};

export const newsletterAPI = {
  getSubscribers: (params = {}) => 
    api.get('/newsletter/subscribers', { params }),
  
  getSubscriber: (id) => 
    api.get(`/newsletter/subscribers/${id}`),
  
  subscribe: (data) => 
    api.post('/newsletter/subscribe', data),
  
  updateSubscriber: (id, data) => 
    api.put(`/newsletter/subscribers/${id}`, data),
  
  deleteSubscriber: (id) => 
    api.delete(`/newsletter/subscribers/${id}`),
  
  unsubscribe: (email) => 
    api.post('/newsletter/unsubscribe', { email }),
  
  verifyEmail: (token) => 
    api.post('/newsletter/verify', { token }),
  
  getCampaigns: (params = {}) => 
    api.get('/newsletter/campaigns', { params }),
  
  getCampaign: (id) => 
    api.get(`/newsletter/campaigns/${id}`),
  
  createCampaign: (campaignData) => 
    api.post('/newsletter/campaigns', campaignData),
  
  updateCampaign: (id, campaignData) => 
    api.put(`/newsletter/campaigns/${id}`, campaignData),
  
  deleteCampaign: (id) => 
    api.delete(`/newsletter/campaigns/${id}`),
  
  sendCampaign: (id, testEmail = null) => 
    api.post(`/newsletter/campaigns/${id}/send`, { testEmail }),
  
  scheduleCampaign: (id, scheduleDate) => 
    api.post(`/newsletter/campaigns/${id}/schedule`, { scheduleDate }),
  
  duplicateCampaign: (id) => 
    api.post(`/newsletter/campaigns/${id}/duplicate`),
  
  getTemplates: () => 
    api.get('/newsletter/templates'),
  
  getTemplate: (id) => 
    api.get(`/newsletter/templates/${id}`),
  
  createTemplate: (templateData) => 
    api.post('/newsletter/templates', templateData),
  
  updateTemplate: (id, templateData) => 
    api.put(`/newsletter/templates/${id}`, templateData),
  
  deleteTemplate: (id) => 
    api.delete(`/newsletter/templates/${id}`),
  
  // Analytics
  getCampaignStats: (id) => 
    api.get(`/newsletter/campaigns/${id}/stats`),
  
  getSubscriberAnalytics: (params = {}) => 
    api.get('/newsletter/analytics', { params }),
  
  trackOpen: (campaignId, subscriberId) => 
    api.post('/newsletter/track/open', { campaignId, subscriberId }),
  
  trackClick: (campaignId, subscriberId, link) => 
    api.post('/newsletter/track/click', { campaignId, subscriberId, link }),
  
  // Bulk operations
  bulkDelete: (ids) => 
    api.post('/newsletter/bulk-delete', { ids }),
  
  bulkUpdateStatus: (ids, status) => 
    api.post('/newsletter/bulk-status', { ids, status }),
  
  importSubscribers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/newsletter/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  exportSubscribers: (format = 'csv') => 
    api.get('/newsletter/export', { 
      params: { format },
      responseType: 'blob'
    }),
};
export const analyticsAPI = {
  getDashboardStats: () => 
    api.get('/analytics/dashboard'),
  
  getProductAnalytics: (params = {}) => 
    api.get('/analytics/products', { params }),
  
  getAppointmentAnalytics: (params = {}) => 
    api.get('/analytics/appointments', { params }),
  
  getMessageAnalytics: (params = {}) => 
    api.get('/analytics/messages', { params }),
  
  getVisitorStats: (params = {}) => 
    api.get('/analytics/visitors', { params }),
  
  exportAnalytics: (type, params = {}) => 
    api.get(`/analytics/export/${type}`, { 
      params,
      responseType: 'blob'
    }),
};

export const newsAPI = {

  getNews: (params = {}) => 
    api.get('/news/home', { params }),
  
  getFeaturedNews: (limit = 3) => 
    api.get('/news/featured', { params: { limit } }),

  getNewsItem: (id) => 
    api.get(`/news/home/${id}`),
  

  getNewsBySlug: (slug) => 
    api.get(`/news/slug/${slug}`),
  

  getNewsByCategory: (categoryId, params = {}) => 
    api.get(`/news/category/${categoryId}`, { params }),
  

  getCategories: () => 
    api.get('/news/categories'),
  
  searchNews: (query, params = {}) => 
    api.get('/news/search', { params: { query, ...params } }),
  

  createNews: (newsData) => {
    const formData = new FormData();
    Object.keys(newsData).forEach(key => {
      if (newsData[key] !== null && newsData[key] !== undefined) {
        if (key === 'image' && newsData[key] instanceof File) {
          formData.append('image', newsData[key]);
        } else if (typeof newsData[key] === 'object') {
          formData.append(key, JSON.stringify(newsData[key]));
        } else {
          formData.append(key, newsData[key]);
        }
      }
    });
    return api.post('/news/admin', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  updateNews: (id, newsData) => {
    const formData = new FormData();
    Object.keys(newsData).forEach(key => {
      if (newsData[key] !== null && newsData[key] !== undefined) {
        if (key === 'image' && newsData[key] instanceof File) {
          formData.append('image', newsData[key]);
        } else if (typeof newsData[key] === 'object') {
          formData.append(key, JSON.stringify(newsData[key]));
        } else {
          formData.append(key, newsData[key]);
        } }
    });
    return api.put(`/news/admin/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  deleteNews: (id) => 
    api.delete(`/news/admin/${id}`),
  
  togglePublish: (id) => 
    api.patch(`/news/admin/${id}/toggle-publish`),
  
  incrementView: (id) => 
    api.post(`/news/admin/${id}/view`),

  bulkDelete: (ids) => 
    api.post('/news/admin/bulk-delete', { ids }),
  
  bulkPublish: (ids) => 
    api.post('/news/admin/bulk-publish', { ids }),
  
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/news/admin/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const uploadAPI = {
  uploadFile: async (file, folder = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    
    return api.post('/content/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(response => {
    
      return response;
    });
  },
  
  deleteFile: (imageUrl) => 
    api.post('/content/delete-image', { url: imageUrl }),
};

export const apiService = {
  auth: authAPI,
  products: productAPI,
  sponsors: sponsorAPI,
  content: contentAPI,
  appointments: appointmentAPI,
  messages: messageAPI,
  users: userAPI,
  analytics: analyticsAPI,
  upload: uploadAPI,
  
  setAuthToken: (token) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  },
  
  setUser: (user) => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  },
  
  clearAuth: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },
  
  request: (config) => api.request(config),
  
  CancelToken: axios.CancelToken,
  isCancel: axios.isCancel,
};

export default api;